import { useState } from 'react'
import { addCategory, getCategories, UNIT_OPTIONS, CATEGORY_TYPES } from '../../data/categories'
import { useToast } from '../../context/ToastContext'
import { X, Plus } from 'lucide-react'
import ArithmeticInput from '../ui/ArithmeticInput'

export default function AddCategoryModal({ onClose, onCategoryAdded }) {
  const { addToast } = useToast()
  const [form, setForm] = useState({
    label: '',
    openingUnit: 'units',
    surplusUnit: 'units',
    salesUnit: 'rands',
    type: 'discrete',
    projectable: true,
    unitPrice: '',
  })

  const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }))

  const handleSave = () => {
    if (!form.label.trim()) {
      addToast('Name is required', 'error')
      return
    }

    const name = form.label.trim().toLowerCase()
    const existing = getCategories()
    if (existing.find(c => c.name === name)) {
      addToast(`Category "${form.label}" already exists`, 'error')
      return
    }

    const category = addCategory({
      name,
      label: form.label.trim(),
      openingUnit: form.openingUnit,
      surplusUnit: form.surplusUnit,
      salesUnit: form.salesUnit,
      type: form.type,
      projectable: form.projectable,
      unitPrice: form.unitPrice ? Number(form.unitPrice) : undefined,
    })

    addToast(`Added meat type "${category.label}"`, 'success')
    onCategoryAdded?.(category)
    onClose()
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-md p-6 space-y-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-white">Add Meat Type</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-white"><X size={18} /></button>
        </div>

        <div>
          <label className="block text-xs text-gray-400 mb-1">Name *</label>
          <input
            value={form.label}
            onChange={e => set('label', e.target.value)}
            className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-white"
            placeholder="e.g. Chicken Wings"
            autoFocus
          />
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="block text-xs text-gray-400 mb-1">Opening Unit</label>
            <select
              value={form.openingUnit}
              onChange={e => set('openingUnit', e.target.value)}
              className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-2 text-sm text-white"
            >
              {UNIT_OPTIONS.map(u => (
                <option key={u.value} value={u.value}>{u.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Surplus Unit</label>
            <select
              value={form.surplusUnit}
              onChange={e => set('surplusUnit', e.target.value)}
              className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-2 text-sm text-white"
            >
              {UNIT_OPTIONS.map(u => (
                <option key={u.value} value={u.value}>{u.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Sales Unit</label>
            <select
              value={form.salesUnit}
              onChange={e => set('salesUnit', e.target.value)}
              className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-2 text-sm text-white"
            >
              {UNIT_OPTIONS.map(u => (
                <option key={u.value} value={u.value}>{u.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-gray-400 mb-1">Category Type</label>
            <select
              value={form.type}
              onChange={e => set('type', e.target.value)}
              className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-2 text-sm text-white"
            >
              {CATEGORY_TYPES.map(t => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>
          {form.type === 'fixed_unit' && (
            <div>
              <label className="block text-xs text-gray-400 mb-1">Fixed Unit Price (R)</label>
              <ArithmeticInput
                min="0"
                value={form.unitPrice}
                onChange={(val) => set('unitPrice', val == null ? '' : val)}
                className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-white"
                placeholder="e.g. 10"
              />
            </div>
          )}
        </div>

        <label className="flex items-center gap-2 text-sm text-gray-300">
          <input
            type="checkbox"
            checked={form.projectable}
            onChange={e => set('projectable', e.target.checked)}
            className="rounded bg-gray-800 border-gray-700"
          />
          Projectable (can calculate expected sales)
        </label>

        <button
          onClick={handleSave}
          className="w-full flex items-center justify-center gap-2 bg-brand-600 hover:bg-brand-500 text-white px-4 py-2.5 rounded-lg text-sm font-medium"
        >
          <Plus size={16} /> Add Meat Type
        </button>
      </div>
    </div>
  )
}
